import React, { useState, useEffect } from "react";
import {
  FiX,
  FiSearch,
  FiDownload,
  FiAlertCircle,
  FiTrendingUp,
  FiArrowRight,
} from "react-icons/fi";
import {
  MdLocationOn,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";
import moment from "moment";
import { images } from "../../utils/images";
import UserAvatar from "../../components/UserAvatar";
import { apiService } from "../../services/apiService";
import { useTripSocket } from "../../utils/SocketTripContext";
import tripSocketInstance from "../../utils/trip-socket";

const fallbackAvatars = [
  "/assets/fdbd134934ac6bbe35cda965b8527d9bae867c95.png",
  "/assets/d26275b10d5cea7e8434182d79c9c8b6a1888e9f.png",
  "/assets/55d90dd9c92f367d926905c2889337c763722410.png",
  "/assets/7fd03bee5254e79b3a2d05dd8df01ed2196a998a.png",
];

const normalizeFleetStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const formatFleetStatusLabel = (value) => {
  if (!value) return "Inactive";
  const str = String(value).trim().toUpperCase();
  if (str === "IDLE") return "Idle";
  if (str === "INACTIVE") return "Inactive";
  if (str === "RUNNING") return "Running";
  if (str === "STOP") return "Stop";
  if (str === "MOVING" || str === "ACTIVE" || str === "AVAILABLE")
    return "Running";
  if (str === "STOPPED" || str === "ALERT") return "Stop";
  if (
    str === "RESTING" ||
    str === "LOADING" ||
    str === "UNLOADING" ||
    str === "FUELING"
  )
    return "Idle";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const formatDrawerDate = (raw) => {
  if (!raw) return "-";
  try {
    const str = String(raw).trim();
    let m;
    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(str)) {
      m = moment.utc(str).utcOffset("+05:00");
    } else {
      m = moment.utc(str.replace(" ", "T")).utcOffset("+05:00");
      if (!m.isValid()) m = moment(str).utcOffset("+05:00");
    }
    if (!m.isValid()) return String(raw);
    return m.format("YYYY-MM-DD hh:mm A");
  } catch {
    return String(raw);
  }
};

const formatDrawerFullDateTime = (raw) => {
  if (!raw) return "-";
  try {
    const str = String(raw).trim();
    let m;
    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(str)) {
      m = moment.utc(str).utcOffset("+05:00");
    } else {
      m = moment.utc(str.replace(" ", "T")).utcOffset("+05:00");
      if (!m.isValid()) m = moment(str).utcOffset("+05:00");
    }
    if (!m.isValid()) return String(raw);
    return m.format("YYYY-MM-DD hh:mm:ss A");
  } catch {
    return String(raw);
  }
};

const mockAttachments = [
  {
    id: 1,
    name: "Receiving Document",
    time: "09-06-2026 11:42 AM",
    imgUrl: "/assets/28a5068b3cf5b72636d0cc2a27184cdf23eb29c7.png",
  },
  {
    id: 2,
    name: "Maintenance Receipt",
    time: "09-06-2026 11:42 AM",
    imgUrl: "/assets/22595d2dd6410311c95c3b20d88a3815bff41e1e.png",
  },
  {
    id: 3,
    name: "Toll Tax",
    time: "09-06-2026 11:42 AM",
    imgUrl: "/assets/16bf3751e53f8b82da65b0a379e33c3c4e36c088.png",
  },
];

const initialStatusLogs = [
  {
    status: "P-1001",
    time: "06:18 AM",
    location: "Lahore Depot",
    coords: "31.5497, 74.3436",
    odo: "47,541",
    by: "Driver",
    src: "Mobile",
  },
  {
    status: "P-1002",
    time: "07:55 AM",
    location: "GT Road, Gujranwala",
    coords: "32.1877, 74.1945",
    odo: "47,652",
    by: "System",
    src: "GPS",
  },
  {
    status: "P-1003",
    time: "09:00 AM",
    location: "Shell Station, Gujrat",
    coords: "32.5711, 74.0753",
    odo: "47,720",
    by: "Driver",
    src: "Mobile",
  },
  {
    status: "P-1005",
    time: "10:00 AM",
    location: "N-5, Sahiwal",
    coords: "30.6682, 73.1114",
    odo: "47,885",
    by: "GPS",
    src: "Telematics",
  },
  {
    status: "P-1006",
    time: "11:30 AM",
    location: "Okara Bypass",
    coords: "30.8100, 73.4500",
    odo: "48,110",
    by: "Driver",
    src: "Mobile",
  },
  {
    status: "P-1007",
    time: "12:15 PM",
    location: "Patoki Toll Plaza",
    coords: "31.0200, 73.8500",
    odo: "48,220",
    by: "System",
    src: "GPS",
  },
  {
    status: "P-1008",
    time: "01:00 PM",
    location: "Manga Mandi",
    coords: "31.2500, 74.0500",
    odo: "48,290",
    by: "GPS",
    src: "Telematics",
  },
  {
    status: "P-1009",
    time: "01:45 PM",
    location: "Thokar Niaz Baig",
    coords: "31.4700, 74.2400",
    odo: "48,320",
    by: "Driver",
    src: "Mobile",
  },
];

const FleetDrawer = ({
  isOpen,
  onClose,
  vehicle,
  detail,
  detailLoading,
  navigate,
  deviceTypeTab = "tracker",
}) => {
  const socketContext = useTripSocket();
  const activeTripSocket = socketContext?.tripSocket || tripSocketInstance;

  const [activeTab, setActiveTab] = useState("Overview");
  const [logSearchText, setLogSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 5;

  const [activities, setActivities] = useState([]);
  const [activitiesTotal, setActivitiesTotal] = useState(0);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Alerts tab paginated state
  const [alerts, setAlerts] = useState([]);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsPage, setAlertsPage] = useState(1);
  const alertsLimit = 5;

  // Attachments tab paginated state
  const [attachments, setAttachments] = useState([]);
  const [attachmentsTotal, setAttachmentsTotal] = useState(0);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsPage, setAttachmentsPage] = useState(1);
  const attachmentsLimit = 6;

  // Real-time trip meta info states from socket
  const [coveredDistance, setCoveredDistance] = useState(null);
  const [coveredPercentage, setCoveredPercentage] = useState(null);
  const [totalDistance, setTotalDistance] = useState(null);

  useEffect(() => {
    const meta = detail?.fmcr_trip?.trip_meta_info;
    const initTotal = Number(
      meta?.total_distance ?? detail?.fmcr_trip?.distance ?? 0,
    );
    const initCovered = Number(
      meta?.covered_distance ?? detail?.fmcr_trip?.covered_distance ?? 0,
    );
    const initPercentage =
      meta?.covered_percentage !== undefined &&
      meta?.covered_percentage !== null
        ? Number(meta.covered_percentage)
        : initTotal > 0
          ? (initCovered / initTotal) * 100
          : 0;

    setTotalDistance(initTotal);
    setCoveredDistance(initCovered);
    setCoveredPercentage(initPercentage);
  }, [detail]);

  const drawerImeino =
    detail?.imeino || detail?.fmcr_trip?.imeino || vehicle?.imeino;

  useEffect(() => {
    if (!isOpen || !drawerImeino || !activeTripSocket) return;

    const eventName = `vehicle-trip-meta-${drawerImeino}`;

    const handleTripMetaUpdate = (data) => {
      console.log("Trip meta socket update received:", eventName, data);
      if (!data) return;

      if (
        data.covered_distance !== undefined &&
        data.covered_distance !== null
      ) {
        setCoveredDistance(Number(data.covered_distance));
      }
      if (
        data.covered_percentage !== undefined &&
        data.covered_percentage !== null
      ) {
        setCoveredPercentage(Number(data.covered_percentage));
      }
      if (data.total_distance !== undefined && data.total_distance !== null) {
        setTotalDistance(Number(data.total_distance));
      }
    };

    activeTripSocket.on(eventName, handleTripMetaUpdate);

    return () => {
      activeTripSocket.off(eventName, handleTripMetaUpdate);
    };
  }, [isOpen, drawerImeino, activeTripSocket]);

  // Pagination helper to avoid rendering too many buttons
  const getVisiblePages = (curr, total) => {
    const maxVisible = 5;
    let start = Math.max(1, curr - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from(
      { length: Math.max(0, end - start + 1) },
      (_, i) => start + i,
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    setActivities([]);
    setActivitiesTotal(0);

    setAlertsPage(1);
    setAlerts([]);
    setAlertsTotal(0);

    setAttachmentsPage(1);
    setAttachments([]);
    setAttachmentsTotal(0);
  }, [vehicle?.id]);

  useEffect(() => {
    if (!vehicle?.id) return;

    const fetchLogs = async () => {
      setActivitiesLoading(true);
      try {
        const response = await apiService.getFmcrDriversActivities(
          {
            vehicle_Ids: [Number(vehicle.id)],
          },
          {
            page: currentPage,
            limit: logsPerPage,
          },
        );
        if (response && response.data) {
          setActivities(response.data.results || []);
          setActivitiesTotal(response.data.total || 0);
        }
      } catch (error) {
        console.error("Failed to fetch driver activities:", error);
      } finally {
        setActivitiesLoading(false);
      }
    };

    fetchLogs();
  }, [vehicle?.id, currentPage]);

  useEffect(() => {
    if (isOpen) {
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!vehicle?.id) return;

    const fetchAlerts = async () => {
      setAlertsLoading(true);
      try {
        const response = await apiService.getFmcrAlerts(
          {
            vehicle_id: Number(vehicle.id),
          },
          {
            page: alertsPage,
            limit: alertsLimit,
          },
        );
        if (response && response.data) {
          setAlerts(response.data.results || []);
          setAlertsTotal(
            response.data.total || response.data.meta?.totalItems || 0,
          );
        }
      } catch (error) {
        console.error("Failed to fetch vehicle alerts:", error);
      } finally {
        setAlertsLoading(false);
      }
    };

    fetchAlerts();
  }, [vehicle?.id, alertsPage]);

  const getTripIdNumber = () => {
    if (detail?.fmcr_trip?.id) {
      return Number(detail.fmcr_trip.id);
    }
    if (vehicle?.tripId) {
      const matched = String(vehicle.tripId).match(/\d+/);
      return matched ? Number(matched[0]) : null;
    }
    return null;
  };

  useEffect(() => {
    const tripId = getTripIdNumber();
    if (!tripId) return;

    const fetchAttachments = async () => {
      setAttachmentsLoading(true);
      try {
        const response = await apiService.getFmcrAttachments(
          {
            trip_id: tripId,
          },
          {
            page: attachmentsPage,
            limit: attachmentsLimit,
          },
        );
        if (response && response.data) {
          setAttachments(response.data.results || []);
          setAttachmentsTotal(
            response.data.total || response.data.meta?.totalItems || 0,
          );
        }
      } catch (error) {
        console.error("Failed to fetch trip attachments:", error);
      } finally {
        setAttachmentsLoading(false);
      }
    };

    fetchAttachments();
  }, [vehicle?.id, detail?.fmcr_trip?.id, attachmentsPage]);

  const handleDownload = (url, name) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = name || "attachment";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = () => {
    attachments.forEach((doc) => {
      const url = doc.imgUrl || doc.url || doc.file_path || doc.path;
      const name =
        doc.name || doc.title || doc.file_name || doc.filename || "attachment";
      if (url) {
        handleDownload(url, name);
      }
    });
  };

  // Alerts acknowledge states
  const [acknowledgedAlerts, setAcknowledgedAlerts] = useState({});

  if (!isOpen || !vehicle) return null;

  const handleAcknowledge = (alertId) => {
    setAcknowledgedAlerts((prev) => ({ ...prev, [alertId]: true }));
  };

  // Filter and Paginate Logs
  const filteredActivities = activities.filter((log) => {
    const term = logSearchText.toLowerCase();
    return (
      !term ||
      String(log.activity_type || "")
        .toLowerCase()
        .includes(term) ||
      String(log.location || "")
        .toLowerCase()
        .includes(term) ||
      String(log.user_type || "")
        .toLowerCase()
        .includes(term)
    );
  });

  const totalPages = Math.max(1, Math.ceil(activitiesTotal / logsPerPage));
  const totalAlertPages = Math.max(1, Math.ceil(alertsTotal / alertsLimit));
  const totalAttachmentPages = Math.max(
    1,
    Math.ceil(attachmentsTotal / attachmentsLimit),
  );

  const logVisiblePages = getVisiblePages(currentPage, totalPages);
  const alertVisiblePages = getVisiblePages(alertsPage, totalAlertPages);
  const attachmentVisiblePages = getVisiblePages(
    attachmentsPage,
    totalAttachmentPages,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Helper to get digits array for ODO Meter
  const getOdoDigits = (odoValue) => {
    if (odoValue === undefined || odoValue === null)
      return ["0", "0", "0", "0", "0"];
    const odoString = String(odoValue);
    const intPart = odoString.split(".")[0];
    const digitsOnly = intPart.replace(/\D/g, "");
    return digitsOnly.split("");
  };

  const odoDigits = getOdoDigits(
    detail?.odm !== undefined ? detail.odm : vehicle.odo,
  );

  const displayTotalDistance = Number(
    totalDistance ??
      detail?.fmcr_trip?.trip_meta_info?.total_distance ??
      detail?.fmcr_trip?.distance ??
      0,
  );

  const displayCoveredDistance = Number(
    coveredDistance ??
      detail?.fmcr_trip?.trip_meta_info?.covered_distance ??
      detail?.fmcr_trip?.covered_distance ??
      0,
  );

  const rawPercent =
    coveredPercentage !== undefined && coveredPercentage !== null
      ? Number(coveredPercentage)
      : detail?.fmcr_trip?.trip_meta_info?.covered_percentage !== undefined &&
          detail?.fmcr_trip?.trip_meta_info?.covered_percentage !== null
        ? Number(detail.fmcr_trip.trip_meta_info.covered_percentage)
        : displayTotalDistance > 0
          ? (displayCoveredDistance / displayTotalDistance) * 100
          : 0;

  const progressPercent = Math.min(100, Math.max(0, rawPercent));

  const remainingDistance =
    displayTotalDistance > 0
      ? Math.max(0, displayTotalDistance - displayCoveredDistance).toFixed(2)
      : "0.00";

  const startTripLocation =
    detail?.fmcr_trip?.start_trip?.location ||
    detail?.fmcr_trip?.stops?.[0]?.location ||
    detail?.fmcr_trip?.trip_current_detail?.location ||
    vehicle?.location ||
    "";

  const startTripProvince =
    detail?.fmcr_trip?.start_trip?.province ||
    detail?.fmcr_trip?.stops?.[0]?.province ||
    "Origin";

  const endTripLocation =
    detail?.fmcr_trip?.end_trip?.location ||
    (detail?.fmcr_trip?.stops && detail.fmcr_trip.stops.length > 1
      ? detail.fmcr_trip.stops[detail.fmcr_trip.stops.length - 1]?.location
      : "");

  const endTripProvince =
    detail?.fmcr_trip?.end_trip?.province ||
    (detail?.fmcr_trip?.stops && detail.fmcr_trip.stops.length > 1
      ? detail.fmcr_trip.stops[detail.fmcr_trip.stops.length - 1]?.province
      : "Destination");

  // Status mapping
  const statusColorMap = {
    running: {
      text: "Running",
      bg: "#ECFDF5",
      color: "#047857",
      dot: "#10B981",
    },
    active: {
      text: "Running",
      bg: "#ECFDF5",
      color: "#047857",
      dot: "#10B981",
    },
    available: {
      text: "Running",
      bg: "#ECFDF5",
      color: "#047857",
      dot: "#10B981",
    },
    moving: {
      text: "Running",
      bg: "#ECFDF5",
      color: "#047857",
      dot: "#10B981",
    },
    in_transit: {
      text: "In Transit",
      bg: "#E8EFFF",
      color: "#1D4ED8",
      dot: "#1D4ED8",
    },
    idle: { text: "Idle", bg: "#FFFBEB", color: "#B45309", dot: "#F59E0B" },
    resting: { text: "Idle", bg: "#FFFBEB", color: "#B45309", dot: "#F59E0B" },
    loading: { text: "Idle", bg: "#FFFBEB", color: "#B45309", dot: "#F59E0B" },
    unloading: {
      text: "Idle",
      bg: "#FFFBEB",
      color: "#B45309",
      dot: "#F59E0B",
    },
    fueling: { text: "Idle", bg: "#FFFBEB", color: "#B45309", dot: "#F59E0B" },
    stop: { text: "Stop", bg: "#FEF2F2", color: "#B91C1C", dot: "#EF4444" },
    stopped: { text: "Stop", bg: "#FEF2F2", color: "#B91C1C", dot: "#EF4444" },
    alert: { text: "Stop", bg: "#FEF2F2", color: "#B91C1C", dot: "#EF4444" },
    inactive: {
      text: "Inactive",
      bg: "#F1F5F9",
      color: "#475569",
      dot: "#94A3B8",
    },
    unknown: {
      text: "Inactive",
      bg: "#F1F5F9",
      color: "#475569",
      dot: "#94A3B8",
    },
  };

  const rawDrawerStatus =
    deviceTypeTab === "tracker"
      ? detail?.tracker_status ||
        vehicle?.tracker_status ||
        detail?.status ||
        vehicle?.status ||
        "INACTIVE"
      : detail?.vehicle_status ||
        vehicle?.vehicle_status ||
        detail?.status ||
        vehicle?.status ||
        "INACTIVE";
  const drawerStatusKey =
    normalizeFleetStatus(rawDrawerStatus) || vehicle?.statusKey || "unknown";
  const statusConfig =
    statusColorMap[drawerStatusKey] || statusColorMap.unknown;
  const drawerStatusLabel = formatFleetStatusLabel(rawDrawerStatus);

  return (
    <>
      {/* Drawer Overlay Backdrop */}
      <div className="fleet_drawer_backdrop" onClick={onClose}></div>

      {/* Drawer Panel */}
      <div className={`fleet_drawer_container ${isOpen ? "open" : ""}`}>
        {/* Drawer Header */}
        <div className="fleet_drawer_header">
          <div className="fleet_drawer_route_info">
            <div
              className="fleet_drawer_route_icon"
              style={{ boxShadow: "none", background: "transparent" }}
            >
              <img
                src={images.startMap}
                alt="Location"
                style={{ width: "34px", height: "34px" }}
              />
            </div>

            {startTripLocation || endTripLocation ? (
              <>
                <div className="fleet_drawer_route_location">
                  <h6>{startTripProvince}</h6>
                  <span title={startTripLocation}>
                    {startTripLocation || "Origin"}
                  </span>
                </div>

                <div className="fleet_drawer_route_connector">
                  <FiArrowRight size="20px" color="#3D3F5C" />
                </div>

                <div className="fleet_drawer_route_location">
                  <h6>{endTripProvince}</h6>
                  <span title={endTripLocation}>
                    {endTripLocation || "Destination"}
                  </span>
                </div>
              </>
            ) : (
              <div className="fleet_drawer_route_location">
                <h6>Current Location</h6>
                <span>
                  {detail?.fmcr_trip?.trip_current_detail?.location ||
                    vehicle?.location ||
                    "No Active Trip Route"}
                </span>
              </div>
            )}
          </div>

          <div className="fleet_drawer_header_actions">
            <span
              className="fleet_drawer_status_badge"
              style={{
                backgroundColor: statusConfig.bg,
                color: statusConfig.color,
              }}
            >
              <span
                className="fleet_drawer_status_dot"
                style={{ backgroundColor: statusConfig.dot }}
              ></span>
              {drawerStatusLabel || "Inactive"}
            </span>

            <button className="fleet_drawer_close_btn" onClick={onClose}>
              <FiX size="18px" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="fleet_drawer_tabs_bar">
          <div className="fleet_drawer_tabs_pill">
            <button
              className={`fleet_drawer_tab_item ${activeTab === "Overview" ? "active" : ""}`}
              onClick={() => setActiveTab("Overview")}
            >
              Overview
            </button>
            <button
              className={`fleet_drawer_tab_item ${activeTab === "Alerts" ? "active" : ""}`}
              onClick={() => setActiveTab("Alerts")}
            >
              Alerts
              {alertsTotal > 0 && (
                <span className="fleet_drawer_alert_badge">
                  {String(alertsTotal).padStart(2, "0")}
                </span>
              )}
            </button>
            <button
              className={`fleet_drawer_tab_item ${activeTab === "Attachments" ? "active" : ""}`}
              onClick={() => setActiveTab("Attachments")}
            >
              Attachments
            </button>
          </div>
        </div>

        {/* Drawer Body Container */}
        <div className="fleet_drawer_body">
          <div className="fleet_drawer_last_updated">
            Last updated:{" "}
            {formatDrawerFullDateTime(
              detail?.updated_at ||
                detail?.last_updated_at ||
                detail?.created_at ||
                vehicle?.lastUpdated,
            )}
          </div>

          {/* Overview Tab Content */}
          {activeTab === "Overview" && (
            <div className="fleet_tab_content_wrapper">
              {detailLoading ? (
                <div className="fleet_drawer_loading_container">
                  <div className="fleet_spinner"></div>
                  <p>Fetching vehicle details...</p>
                </div>
              ) : (
                <>
                  {/* Summary Profile Cards Row */}
                  <div className="fleet_drawer_profile_grid">
                    {/* Vehicle Card */}
                    <div className="fleet_drawer_profile_card">
                      <div className="fleet_drawer_card_top">
                        <div className="fleet_drawer_card_avatar_wrapper">
                          <div className="fleet_drawer_vehicle_avatar">
                            <img
                              src={
                                fallbackAvatars[vehicle?.avatarIdx] ||
                                images.vehcileImg
                              }
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = images.vehcileImg || "";
                              }}
                              alt={
                                detail?.registration_number ||
                                vehicle?.regNumber
                              }
                            />
                          </div>
                          <div className="fleet_drawer_vehicle_meta">
                            <span>
                              {detail?.load_type
                                ? String(detail.load_type)
                                    .replace(/_/g, " ")
                                    .toUpperCase()
                                : detail?.fuel_type
                                  ? `${String(detail.fuel_type).toUpperCase()} • ${String(detail?.ownership_type || "COMMERCIAL").toUpperCase()}`
                                  : "COMMERCIAL VEHICLE"}
                            </span>
                            <h6>
                              {detail?.registration_number ||
                                vehicle?.regNumber ||
                                "-"}
                            </h6>
                          </div>
                        </div>
                        <FiTrendingUp className="fleet_drawer_card_arrow_icon" />
                      </div>

                      <div className="fleet_drawer_card_inputs_row">
                        <div className="fleet_drawer_stat_box">
                          <label>ODO Meter</label>
                          <div className="fleet_drawer_odo_digits_row">
                            {odoDigits.map((digit, index) => (
                              <span
                                key={index}
                                className="fleet_drawer_odo_digit"
                              >
                                {digit}
                              </span>
                            ))}
                            <span className="fleet_drawer_odo_unit">km</span>
                          </div>
                        </div>

                        <div className="fleet_drawer_stat_box">
                          <label>Current Speed</label>
                          <div className="fleet_drawer_speed_value">
                            <FiTrendingUp
                              size="16px"
                              className="fleet_drawer_speed_icon"
                            />
                            <strong>
                              {vehicle?.speed
                                ? String(vehicle.speed).split(" ")[0]
                                : "0"}
                            </strong>
                            <span>km/hr</span>
                          </div>
                        </div>
                      </div>

                      <div className="fleet_drawer_distance_progress_section">
                        <div className="fleet_drawer_progress_labels">
                          <span className="fleet_distance_title">Distance</span>
                          <span className="fleet_distance_totals">
                            <strong>
                              {displayTotalDistance > 0
                                ? displayTotalDistance.toFixed(2)
                                : "--"}
                            </strong>{" "}
                            km /{" "}
                            <strong className="fleet_progress_green">
                              {displayCoveredDistance.toFixed(2)}
                            </strong>{" "}
                            km (
                            <span className="fleet_progress_green_pct">
                              {progressPercent.toFixed(1)}%
                            </span>
                            )
                          </span>
                        </div>
                        <div className="fleet_drawer_progress_track">
                          <div
                            className="fleet_drawer_progress_fill"
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                          <div
                            className="fleet_drawer_progress_indicator"
                            style={{ left: `${progressPercent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Driver Card */}
                    <div className="fleet_drawer_profile_card">
                      <div className="fleet_drawer_card_top">
                        <div className="fleet_drawer_card_avatar_wrapper">
                          <UserAvatar
                            username={
                              detail?.primary_driver?.username ||
                              vehicle?.driver ||
                              "Unassigned"
                            }
                            image={detail?.primary_driver?.image}
                            size={54}
                            fontSize="20px"
                            style={{ objectFit: "cover" }}
                          />
                          <div className="fleet_drawer_driver_meta">
                            <span>Vehicle Driver</span>
                            <h6
                              onClick={() => navigate("/driver-safety")}
                              className="fleet_drawer_driver_link"
                            >
                              {detail?.primary_driver?.username ||
                                vehicle?.driver ||
                                "Unassigned"}
                            </h6>
                          </div>
                        </div>
                        <FiTrendingUp className="fleet_drawer_card_arrow_icon" />
                      </div>

                      <div
                        className="fleet_drawer_card_inputs_row"
                        style={{ marginTop: "auto" }}
                      >
                        <div className="fleet_drawer_stat_box">
                          <label>Mobile Number</label>
                          <span className="fleet_drawer_stat_val">
                            {detail?.primary_driver?.mobile ||
                              detail?.fmcr_trip?.primary_driver_contact ||
                              vehicle?.phone ||
                              "-"}
                          </span>
                        </div>

                        <div className="fleet_drawer_stat_box">
                          <label>Status</label>
                          <span
                            className="fleet_drawer_status_active"
                            style={{ textTransform: "capitalize" }}
                          >
                            <span className="fleet_active_dot"></span>{" "}
                            {detail?.primary_driver ? "Active" : "Unassigned"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trip Details Grid */}
                  <div className="fleet_drawer_metrics_grid">
                    <div className="fleet_drawer_metric_item orange_highlight">
                      <span className="fleet_metric_label">
                        Remaining Distance
                      </span>
                      <span className="fleet_metric_value">
                        <strong>
                          {displayTotalDistance > 0
                            ? `${remainingDistance} km`
                            : "--"}
                        </strong>
                      </span>
                    </div>

                    <div
                      className="fleet_drawer_metric_item link_highlight"
                      onClick={() =>
                        detail?.fmcr_trip?.id &&
                        navigate(`/trips/${detail.fmcr_trip.id}`)
                      }
                    >
                      <span className="fleet_metric_label">
                        Current Trip ID
                      </span>
                      <span className="fleet_metric_value text_link">
                        {detail?.fmcr_trip?.trip_id ||
                          (detail?.fmcr_trip?.id
                            ? `TR-${detail.fmcr_trip.id}`
                            : vehicle?.tripId || "No Active Trip")}
                      </span>
                    </div>

                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">Planned Start</span>
                      <span className="fleet_metric_value">
                        {formatDrawerDate(detail?.fmcr_trip?.start_at)}
                      </span>
                    </div>

                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">Actual Start</span>
                      <span className="fleet_metric_value">
                        {formatDrawerDate(detail?.fmcr_trip?.actual_start_at)}
                      </span>
                    </div>

                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">Planned End</span>
                      <span className="fleet_metric_value">
                        {formatDrawerDate(detail?.fmcr_trip?.expected_end_at)}
                      </span>
                    </div>

                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">ETA</span>
                      <span className="fleet_metric_value">
                        {formatDrawerDate(
                          detail?.fmcr_trip?.actual_end_at ||
                            detail?.fmcr_trip?.expected_end_at,
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Technical Specifications Section */}
                  <div
                    className="fleet_drawer_section_title"
                    style={{ marginTop: "20px", marginBottom: "10px" }}
                  >
                    <h5
                      style={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#3D3F5C",
                        margin: 0,
                      }}
                    >
                      Technical Specifications
                    </h5>
                  </div>
                  <div
                    className="fleet_drawer_metrics_grid"
                    style={{ marginBottom: "15px" }}
                  >
                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">Chassis Number</span>
                      <span className="fleet_metric_value">
                        {detail?.chassis_number || "-"}
                      </span>
                    </div>
                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">Engine Number</span>
                      <span className="fleet_metric_value">
                        {detail?.engine_number || "-"}
                      </span>
                    </div>
                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">VIN</span>
                      <span className="fleet_metric_value">
                        {detail?.vin || "-"}
                      </span>
                    </div>
                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">IMEI No</span>
                      <span className="fleet_metric_value">
                        {detail?.imeino || vehicle?.imeino || "-"}
                      </span>
                    </div>
                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">Fuel Type</span>
                      <span
                        className="fleet_metric_value"
                        style={{ textTransform: "capitalize" }}
                      >
                        {detail?.fuel_type || "-"}
                      </span>
                    </div>
                    <div className="fleet_drawer_metric_item">
                      <span className="fleet_metric_label">Ownership Type</span>
                      <span
                        className="fleet_metric_value"
                        style={{ textTransform: "capitalize" }}
                      >
                        {detail?.ownership_type || "-"}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Alerts Tab Content */}
          {activeTab === "Alerts" && (
            <div className="fleet_tab_content_wrapper">
              <div className="fleet_alerts_list">
                {alertsLoading ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "40px 20px",
                      gap: "8px",
                    }}
                  >
                    <div
                      className="fleet_spinner"
                      style={{
                        width: "32px",
                        height: "32px",
                        borderWidth: "4px",
                      }}
                    ></div>
                    <span style={{ color: "#8E91A8", fontSize: "14px" }}>
                      Loading alerts...
                    </span>
                  </div>
                ) : alerts.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#8E91A8",
                      fontSize: "14px",
                    }}
                  >
                    No active alerts found for this vehicle.
                  </div>
                ) : (
                  <>
                    {alerts.map((alertItem) => {
                      const type = String(alertItem.type || "").toLowerCase();
                      const priority = String(
                        alertItem.priority || "",
                      ).toLowerCase();

                      let cardClass = "alert_success";
                      let iconColor = "#2fb019";

                      if (
                        priority === "high" ||
                        type.includes("speed") ||
                        type.includes("brake") ||
                        type.includes("danger")
                      ) {
                        cardClass = "alert_danger";
                        iconColor = "#EF0000";
                      } else if (
                        priority === "medium" ||
                        type.includes("battery") ||
                        type.includes("warning")
                      ) {
                        cardClass = "alert_warning";
                        iconColor = "#4014B8";
                      }

                      const formattedType = String(alertItem.type || "")
                        .split("_")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1),
                        )
                        .join(" ");

                      const timeStr =
                        alertItem.occurred_at || alertItem.created_at
                          ? new Date(
                              alertItem.occurred_at || alertItem.created_at,
                            ).toLocaleString([], {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-";

                      return (
                        <div
                          className={`fleet_alert_card ${cardClass}`}
                          key={alertItem.id}
                        >
                          <div className="fleet_alert_icon_col">
                            <FiAlertCircle size="20px" color={iconColor} />
                          </div>
                          <div className="fleet_alert_info_col">
                            <h6>{formattedType}</h6>
                            <span className="fleet_alert_desc">
                              {alertItem.description ||
                                alertItem.location ||
                                "-"}
                            </span>
                            <span className="fleet_alert_timestamp">
                              {timeStr}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Alerts Pagination Bar */}
                    {totalAlertPages > 1 && (
                      <div
                        className="fleet_pagination_bar"
                        style={{ marginTop: "16px" }}
                      >
                        <span className="fleet_pagination_total">
                          Total {alertsTotal} items
                        </span>
                        <div className="fleet_pagination_controls">
                          <button
                            className="fleet_page_arrow"
                            disabled={alertsPage === 1}
                            onClick={() => setAlertsPage(alertsPage - 1)}
                          >
                            <MdKeyboardArrowLeft size="18px" />
                          </button>

                          {alertVisiblePages.map((page) => (
                            <button
                              key={page}
                              className={`fleet_page_number ${alertsPage === page ? "active" : ""}`}
                              onClick={() => setAlertsPage(page)}
                            >
                              {page}
                            </button>
                          ))}

                          <button
                            className="fleet_page_arrow"
                            disabled={alertsPage === totalAlertPages}
                            onClick={() => setAlertsPage(alertsPage + 1)}
                          >
                            <MdKeyboardArrowRight size="18px" />
                          </button>
                        </div>
                        <span className="fleet_page_size_label">
                          {alertsLimit} / page
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Attachments Tab Content */}
          {activeTab === "Attachments" && (
            <div className="fleet_tab_content_wrapper">
              {attachmentsLoading ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "40px 20px",
                    gap: "8px",
                  }}
                >
                  <div
                    className="fleet_spinner"
                    style={{
                      width: "32px",
                      height: "32px",
                      borderWidth: "4px",
                    }}
                  ></div>
                  <span style={{ color: "#8E91A8", fontSize: "14px" }}>
                    Loading attachments...
                  </span>
                </div>
              ) : attachments.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "#8E91A8",
                    fontSize: "14px",
                  }}
                >
                  No attachments found for this trip.
                </div>
              ) : (
                <>
                  <div className="fleet_attachments_header_row">
                    <button
                      className="fleet_download_all_btn"
                      onClick={handleDownloadAll}
                    >
                      <FiDownload size="15px" />
                      Download All
                    </button>
                  </div>

                  <div className="fleet_attachments_grid">
                    {attachments.map((doc) => {
                      const name =
                        doc.name ||
                        doc.title ||
                        doc.file_name ||
                        doc.filename ||
                        "Attachment";
                      const imgUrl =
                        doc.imgUrl ||
                        doc.url ||
                        doc.file_path ||
                        doc.path ||
                        "";
                      const time =
                        doc.created_at || doc.timestamp
                          ? new Date(
                              doc.created_at || doc.timestamp,
                            ).toLocaleString([], {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : doc.time || "-";

                      return (
                        <div className="fleet_attachment_card" key={doc.id}>
                          <div className="fleet_attachment_preview">
                            {imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={name}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = images.vehcileImg || "";
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  height: "100%",
                                  color: "#BCC0E0",
                                }}
                              >
                                <FiDownload size="24px" />
                              </div>
                            )}
                          </div>
                          <div className="fleet_attachment_details">
                            <h6>{name}</h6>
                            <span>{time}</span>
                          </div>
                          <div className="fleet_attachment_actions">
                            <button
                              className="fleet_attachment_preview_btn"
                              onClick={() =>
                                imgUrl && window.open(imgUrl, "_blank")
                              }
                              disabled={!imgUrl}
                            >
                              Preview
                            </button>
                            <button
                              className="fleet_attachment_dl_icon_btn"
                              onClick={() =>
                                imgUrl && handleDownload(imgUrl, name)
                              }
                              disabled={!imgUrl}
                            >
                              <FiDownload size="14px" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Attachments Pagination Bar */}
                  {totalAttachmentPages > 1 && (
                    <div
                      className="fleet_pagination_bar"
                      style={{ marginTop: "20px" }}
                    >
                      <span className="fleet_pagination_total">
                        Total {attachmentsTotal} items
                      </span>
                      <div className="fleet_pagination_controls">
                        <button
                          className="fleet_page_arrow"
                          disabled={attachmentsPage === 1}
                          onClick={() =>
                            setAttachmentsPage(attachmentsPage - 1)
                          }
                        >
                          <MdKeyboardArrowLeft size="18px" />
                        </button>

                        {attachmentVisiblePages.map((page) => (
                          <button
                            key={page}
                            className={`fleet_page_number ${attachmentsPage === page ? "active" : ""}`}
                            onClick={() => setAttachmentsPage(page)}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          className="fleet_page_arrow"
                          disabled={attachmentsPage === totalAttachmentPages}
                          onClick={() =>
                            setAttachmentsPage(attachmentsPage + 1)
                          }
                        >
                          <MdKeyboardArrowRight size="18px" />
                        </button>
                      </div>
                      <span className="fleet_page_size_label">
                        {attachmentsLimit} / page
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FleetDrawer;
